// eslint-disable-next-line benchlint/no-direct-import-use-flags
import {useFlags} from 'launchdarkly-react-client-sdk';
import type {LDFlagSet} from 'launchdarkly-react-client-sdk';
import {kebabCase, camelCase, snakeCase} from 'lodash';

import BenchlingConfig from 'BenchlingConfig';
import {isNodeTesting} from 'lib/Browser';
import {useAppSelector} from 'redux-stores/reduceApp';

export interface LaunchDarklyContext {
  kind: 'user';
  key: string;
  name: string;
  subdomain: string;
  stack: string;
  deploy: string;
  environment: string;
}

const BROWSER_TESTING_PREFIX = 'browsertesting';

/**
 * Gets the User object for LaunchDarkly. This object represents a tenant, and contains several infra-related
 * fields relevant to a tenant: friendly name, subdomain, stack name, deploy name, and deploy environment.
 * It also contains the tenant's API ID.
 *
 * Infra attributes included in the context:
 * - `subdomain`: the tenant's subdomain (e.g. "revmed.benchling.com")
 * - `stack`: the stack name (e.g. "main")
 * - `deploy`: the deploy name sourced from DEPLOY_NAME (e.g. "main")
 * - `environment`: the deploy environment sourced from DEPLOY_ENVIRONMENT (e.g. "latest", "stable")
 *
 * Both `deploy` and `environment` are included to match the backend LaunchDarkly context. `deploy` identifies
 * which named deploy the tenant is on, while `environment` identifies the release stage of that deploy.
 *
 * If called in a browser testing context, most of the fields will be clobbered with `browsertesting` in order
 * to avoid running up the LD bill, which is calculated per-unique-user.
 */
export function getLaunchDarklyContext(): LaunchDarklyContext {
  const isBrowserTesting = BenchlingConfig.TENANT_SUBDOMAIN.startsWith(BROWSER_TESTING_PREFIX);

  return {
    kind: 'user',
    key: isBrowserTesting ? BROWSER_TESTING_PREFIX : BenchlingConfig.TENANT_ID,
    name: isBrowserTesting ? BROWSER_TESTING_PREFIX : BenchlingConfig.TENANT_NAME,
    subdomain: isBrowserTesting ? BROWSER_TESTING_PREFIX : BenchlingConfig.TENANT_SUBDOMAIN,
    stack: BenchlingConfig.STACK_NAME,
    deploy: BenchlingConfig.DEPLOY_NAME,
    environment: BenchlingConfig.DEPLOY_ENVIRONMENT,
  };
}

const useMockLDFlags = (): LDFlagSet => useAppSelector((state) => state.ui.mockLaunchDarkly.flags);

/**
 * `useRolloutFlags` is a custom wrapper around LaunchDarkly's `useFlags` which returns all feature flags provided
 * by `LaunchDarklyProvider`. During a unit test (when `isNodeTesting` is true), it will instead read from the Redux store.
 *
 * This is necessary because we don't want to access the live LD service while unit testing and we can't mock
 * `useFlags` directly because the canonical way to mock LD relies on Jest's ability to globally mock modules,
 * which Sinon / proxyquire cannot do.
 *
 * @return A set of feature flag values
 */
export const useRolloutFlags = isNodeTesting ? useMockLDFlags : useFlags;

export function extendFlagCasing(newFlags: LDFlagSet, currentFlags: LDFlagSet = {}): LDFlagSet {
  const extendedFlags = {...currentFlags};

  // LD's official Jest mock does this flag casing
  // https://github.com/launchdarkly/jest-launchdarkly-mock/blob/ac325e6da7c220513f6c56521d74d70fe34b53fb/src/index.ts#L60
  Object.keys(newFlags).forEach((k) => {
    const kebab = kebabCase(k);
    const camel = camelCase(k);
    const snake = snakeCase(k);
    extendedFlags[kebab] = newFlags[k];
    extendedFlags[camel] = newFlags[k];
    extendedFlags[snake] = newFlags[k];
    extendedFlags[k] = newFlags[k];
  });

  return extendedFlags;
}
